import {
  type GqlCompany,
  type GqlCompleteShareCapitalChange,
  type GqlShareTransactionGroup,
  type GqlSnrShareCapitalChange,
  useSnrCasesQuery,
} from '../../../../graphql/react-operations';
import { DateTime } from 'luxon';
import { maybeDate } from './valid-dates';
import { snrCasesBetweenSccs } from './snr-cases-between';
import { type GqlSnrCase, GqlTransactionKind } from '../../../../graphql/base-types.graphql';

export const isFoundation = (next: { name: string }) => next.name.startsWith('NYBILDNING');

export const useDefaultDateForTransactionGroup = (
  kind: GqlTransactionKind | 'next',
  shouldFetch: boolean,
  prev: Pick<GqlSnrShareCapitalChange, 'filingYear' | 'filingNumber'> | null | undefined,
  next:
    | Pick<GqlCompleteShareCapitalChange, 'filingYear' | 'filingNumber' | 'name' | 'decisionDate' | 'registrationDate'>
    | null
    | undefined,
  minDate: DateTime | undefined,
  shareTransactionGroups: Pick<GqlShareTransactionGroup, 'transactionDate'>[],
  company: Pick<GqlCompany, 'id' | 'foundationDate'>,
) => {
  const { data: casesData } = useSnrCasesQuery({
    variables: { companyId: company.id },
    skip: !shouldFetch,
    fetchPolicy: 'no-cache',
  });
  const snrCases = casesData?.snrCases.models;
  return defaultDateForTransactionGroup(kind, prev, next, minDate, shareTransactionGroups, company, snrCases);
};

/**
 * Exposed for testability, use the hook instead
 */
export const defaultDateForTransactionGroup = (
  kind: GqlTransactionKind | 'next',
  prev: Pick<GqlSnrShareCapitalChange, 'filingYear' | 'filingNumber'> | null | undefined,
  next:
    | Pick<GqlCompleteShareCapitalChange, 'filingYear' | 'filingNumber' | 'name' | 'decisionDate' | 'registrationDate'>
    | null
    | undefined,
  minDate: DateTime | undefined,
  shareTransactionGroups: Pick<GqlShareTransactionGroup, 'transactionDate'>[],
  company: Pick<GqlCompany, 'foundationDate'>,
  snrCases: Pick<GqlSnrCase, 'filingYear' | 'filingNumber' | 'subjects'>[] | null | undefined,
) => {
  if (next && isFoundation(next)) return DateTime.fromISO(company.foundationDate);
  if (kind === GqlTransactionKind.Transfer || kind === GqlTransactionKind.ShareClassConvert) {
    return DateTime.fromISO(shareTransactionGroups.at(-1)!.transactionDate);
  }
  if (!next && kind === GqlTransactionKind.Issue) {
    return DateTime.now().startOf('day');
  }
  if (kind === GqlTransactionKind.Merge || kind === GqlTransactionKind.Split) {
    if (!next) {
      return DateTime.now().startOf('day');
    }
    const relevantSnrCases = snrCases ? snrCasesBetweenSccs(snrCases, prev, next) : undefined;
    const arrivalDate = maybeDate(relevantSnrCases?.[0]?.arrivalDate);
    if (!arrivalDate || (minDate && arrivalDate.toMillis() < minDate.toMillis())) {
      return minDate;
    }
    return arrivalDate;
  }
  return maybeDate(next?.decisionDate) ?? maybeDate(next?.registrationDate) ?? DateTime.now().startOf('day');
};
